import { useEffect, useState } from "react";
import { API, apiError, formatDateTime } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Trash2, Edit2, X, Building2, Lock } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", location: "", phone: "", pharmacy_name: "", address: "", gst: "", email: "",
  head_name: "", head_email: "", head_password: "",
};

export default function Branches() {
  const { user } = useAuth();
  const canWrite = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [headCreated, setHeadCreated] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await API.get("/branches"); setItems(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setModal("new"); };
  const openEdit = (b) => {
    setForm({
      name: b.name || "", location: b.location || "", phone: b.phone || "",
      pharmacy_name: b.pharmacy_name || "", address: b.address || "",
      gst: b.gst || "", email: b.email || "",
    });
    setModal(b);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (modal === "new") {
        const { data } = await API.post("/branches", form);
        if (data?.branch_head) {
          setHeadCreated({ branchName: form.name, ...data.branch_head, password: form.head_password });
          toast.success(`Branch + login created`);
        } else {
          toast.success("Branch created");
        }
      } else {
        await API.put(`/branches/${modal.id}`, form);
        toast.success("Saved");
      }
      setModal(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete branch ${b.name}?`)) return;
    try { await API.delete(`/branches/${b.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="branches-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Locations</div>
          <h1 className="font-display text-3xl font-bold">Branch Management</h1>
          <p className="text-sm text-slate-500 mt-1">Each branch has its own pharmacy name, GST and address — printed on its invoices.</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="add-branch-btn" disabled={!canWrite} title={canWrite ? "" : "Only platform admin can create branches"}><Plus className="w-4 h-4" /> New Branch</button>
      </div>

      {!canWrite && (
        <div className="flex items-center gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="branches-readonly-notice">
          <Lock className="w-3.5 h-3.5 text-amber-600" />
          <span>You have view-only access to branches. Only a <b>platform admin</b> can create, edit or delete branches.</span>
        </div>
      )}

      {loading ? <div className="text-slate-500 text-sm">Loading…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((b) => (
            <div key={b.id} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col" data-testid={`branch-${b.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-[#002FA7]/5 text-[#002FA7] flex items-center justify-center">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(b)} disabled={!canWrite} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#002FA7] disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed" title={canWrite ? "Edit" : "Admin-only"}><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(b)} disabled={!canWrite} className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed" title={canWrite ? "Delete" : "Admin-only"}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <h3 className="font-display text-lg font-semibold mt-3">{b.name}</h3>
              {b.pharmacy_name && <div className="text-xs text-[#002FA7] font-semibold mt-0.5">{b.pharmacy_name}</div>}
              <div className="text-sm text-slate-500 mt-1">{b.location}</div>
              {b.address && <div className="text-xs text-slate-500 mt-1">{b.address}</div>}
              <div className="mt-2 space-y-0.5 text-xs text-slate-500">
                {b.phone && <div className="font-mono">{b.phone}</div>}
                {b.gst && <div className="font-mono">GST: {b.gst}</div>}
                {b.email && <div>{b.email}</div>}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center">
                <span className="tag tag-info">{b.user_count ?? 0} staff</span>
                <span className="text-xs text-slate-400">{formatDateTime(b.created_at)}</span>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="col-span-full text-sm text-slate-500 text-center p-10">No branches yet.</div>}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-display text-xl font-semibold">{modal === "new" ? "New Branch" : "Edit Branch"}</h3>
              <button type="button" onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <L label="Branch Name (internal) *"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" placeholder="e.g. CP Outlet" data-testid="branch-name" /></L>
              <L label="Pharmacy Name (on bills)"><input value={form.pharmacy_name} onChange={(e) => setForm({ ...form, pharmacy_name: e.target.value })} className="inp" placeholder="e.g. MedOne Pharma — CP" data-testid="branch-pharmacy-name" /></L>
              <L label="Location *"><input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="inp" data-testid="branch-location" /></L>
              <L label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="inp" /></L>
              <L label="GST Number"><input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} className="inp font-mono" /></L>
              <L label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" /></L>
              <L label="Full Address (printed on invoices)" full>
                <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="inp resize-none" />
              </L>

              {modal === "new" && (
                <>
                  <div className="md:col-span-2 mt-2 pt-3 border-t border-slate-200">
                    <div className="overline mb-1">Branch Head Login (optional)</div>
                    <p className="text-[11px] text-slate-500 mb-2">
                      Create a login for this branch's head. They'll see only this branch's medicines, stock, sales & reports — fully isolated from other branches.
                    </p>
                  </div>
                  <L label="Head Name">
                    <input value={form.head_name} onChange={(e) => setForm({ ...form, head_name: e.target.value })} className="inp" placeholder="e.g. Anita Sharma" data-testid="branch-head-name" />
                  </L>
                  <L label="Head Login Email">
                    <input type="email" value={form.head_email} onChange={(e) => setForm({ ...form, head_email: e.target.value })} className="inp" placeholder="head.cp@medicore.com" data-testid="branch-head-email" autoComplete="off" />
                  </L>
                  <L label="Temporary Password (min 6 chars)" full>
                    <input type="text" value={form.head_password} onChange={(e) => setForm({ ...form, head_password: e.target.value })} className="inp font-mono" placeholder="leave empty to skip" data-testid="branch-head-password" autoComplete="new-password" />
                  </L>
                </>
              )}
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-branch-btn">Save</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #E2E8F0;border-radius:8px;padding:.5rem .75rem;font-size:.875rem;outline:none}.inp:focus{border-color:#002FA7;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>

      {headCreated && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="branch-head-created-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              <h3 className="font-display text-lg font-semibold">Branch & Login Created</h3>
            </div>
            <p className="text-sm text-slate-600">
              Branch <b>{headCreated.branchName}</b> is ready. Share these credentials securely with <b>{headCreated.name}</b>:
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 font-mono text-xs space-y-1">
              <div><span className="text-slate-500">Email:</span> <span className="font-semibold">{headCreated.email}</span></div>
              <div><span className="text-slate-500">Password:</span> <span className="font-semibold">{headCreated.password}</span></div>
              <div><span className="text-slate-500">Role:</span> Branch Head (isolated to this branch)</div>
            </div>
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              This password is shown only once — copy it now. They can change it from Users page after first login.
            </p>
            <div className="flex justify-end">
              <button onClick={() => setHeadCreated(null)} className="btn-primary" data-testid="close-head-created">Got it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function L({ label, children, full }) { return <label className={`block ${full ? "md:col-span-2" : ""}`}><div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>{children}</label>; }
